import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { AuthUser } from '../api/types'
import { apiLogin, apiLogout, apiMe } from '../api/client'
import { setUnauthorizedHandler } from './session'

/**
 * Estado de autenticación de la aplicación — SEC-001.
 *
 * El usuario vive **solo en memoria de React**. No se persiste nada: no hay token que
 * guardar, y guardar el perfil tampoco aportaría, porque quien decide si la sesión sigue
 * viva es el servidor. La rehidratación al cargar la página consiste en preguntárselo con
 * `GET /api/auth/me`, que valida la cookie contra la sesión en base de datos. Una sesión
 * revocada deja de rehidratarse de inmediato, cosa que un perfil cacheado en
 * `localStorage` no permitiría.
 */

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  initializing: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initializing, setInitializing] = useState(true)

  // Rehidratación: la fuente de verdad es el servidor, no el almacenamiento del navegador.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const current = await apiMe()
        if (!cancelled) setUser(current)
      } catch {
        // Sin cookie, o con una sesión revocada o vencida: simplemente no hay sesión.
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setInitializing(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const logout = useCallback(async () => {
    // Se llama al servidor **antes** de limpiar el estado: la revocación es lo que cierra
    // la sesión de verdad. Limpiar primero solo ocultaría una sesión que sigue viva.
    await apiLogout()
    setUser(null)
  }, [])

  // Si una llamada protegida recibe 401, el estado local ya no corresponde a nada.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    // La respuesta no trae ningún token: el navegador ya recibió las cookies en
    // `Set-Cookie` y aquí solo se conserva el perfil público para la interfaz.
    const result = await apiLogin(email, password)
    setUser(result.user)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, initializing, login, logout }),
    [user, initializing, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>')
  }
  return ctx
}
